import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { SnackbarProvider } from 'notistack';
import { Alert } from '@mui/material';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Initialize AWS Amplify configuration
import './aws-config';

// Import themes
import cleanTheme, { cleanLightTheme } from './theme-clean';

// Import motion config to disable animations
import { disableFramerMotion, setReducedMotion } from './utils/motionConfig';

// Import pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import AuthCallback from './pages/AuthCallback';
import Profile from './pages/Profile';
import EnvDebug from './components/EnvDebug';
import ViewSnippet from './pages/ViewSnippet';
import ViewDocument from './pages/ViewDocument';
import MySnippets from './pages/MySnippets';
import MyDocuments from './pages/MyDocuments';
import CreateSnippet from './pages/CreateSnippet';
import TextEditorPage from './pages/TextEditorPage';

import NotFound from './pages/NotFound';
// Import components
import ImprovedSidebar from './components/ImprovedSidebar';
import { AuthGuard } from './components';

// Import context providers
import { AuthProvider } from './context/AuthContext';
import { ModelProvider } from './context/ModelContext';
import { FolderProvider } from './context/FolderContext';
import { AIProvider } from './context/AIContext';
import { EditorProvider } from './context/EditorContext';

// Error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by boundary:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            Something went wrong. Please try refreshing the page.
          </Alert>
          <pre style={{ whiteSpace: 'pre-wrap' }}>
            {this.state.error && this.state.error.toString()}
          </pre>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Layout component to conditionally show sidebar
function AppLayout({ children, onToggleTheme, darkMode }) {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';

  if (isLoginPage) {
    return <>{children}</>;
  }

  // All other pages use the improved sidebar layout
  return (
    <ImprovedSidebar onToggleTheme={onToggleTheme} darkMode={darkMode}>
      {children}
    </ImprovedSidebar>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    // Default to light mode (false) if no saved preference exists
    return savedMode !== null ? JSON.parse(savedMode) : false;
  });

  // Disable all animations on app startup
  useEffect(() => {
    disableFramerMotion();
    setReducedMotion();
  }, []);

  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleColorMode = () => {
    setDarkMode(!darkMode);
  };

  const currentTheme = darkMode ? cleanTheme : cleanLightTheme;

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <AuthProvider>
          <ModelProvider>
            <FolderProvider>
              <AIProvider>
                <EditorProvider>
                <SnackbarProvider 
                  maxSnack={3} 
                  anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                  autoHideDuration={4000}
                >
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true
                }}
              >
                <AppLayout onToggleTheme={toggleColorMode} darkMode={darkMode}>
                  <ErrorBoundary>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/callback" element={<AuthCallback />} />
                      <Route path="/snippet/:id" element={<ViewSnippet />} />
                      <Route path="/document/:id" element={<ViewDocument />} />

                      {/* Routes accessible to guests */}
                      <Route path="/" element={
                        <AuthGuard>
                          <Dashboard />
                        </AuthGuard>
                      } />
                      <Route path="/create" element={
                        <AuthGuard>
                          <CreateSnippet />
                        </AuthGuard>
                      } />
                      <Route path="/docs" element={
                        <AuthGuard>
                          <TextEditorPage />
                        </AuthGuard>
                      } />
                      
                      {/* Routes requiring full authentication */}
                      <Route path="/snippets" element={
                        <AuthGuard requireAuth={true}>
                          <MySnippets />
                        </AuthGuard>
                      } />
                      <Route path="/documents" element={
                        <AuthGuard requireAuth={true}>
                          <MyDocuments />
                        </AuthGuard>
                      } />

                      <Route path="/profile" element={
                        <AuthGuard requireAuth={true}>
                          <Profile />
                        </AuthGuard>
                      } />
                      
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </ErrorBoundary>
                </AppLayout>
              </Router>
            </SnackbarProvider>
                </EditorProvider>
              </AIProvider>
            </FolderProvider>
          </ModelProvider>
        </AuthProvider>
      </GoogleOAuthProvider>
      <EnvDebug />
    </ThemeProvider>
  );
}

export default App;